import type { OrganisationNode } from "@models/access";

/** Every organisation of the tree, depth first, with its depth (root = 0). */
export const flattenTree = (roots: OrganisationNode[]): Array<OrganisationNode & { depth: number }> => {
  const out: Array<OrganisationNode & { depth: number }> = [];
  const walk = (node: OrganisationNode, depth: number) => {
    out.push({ ...node, depth });
    node.children.forEach((child) => walk(child, depth + 1));
  };
  roots.forEach((root) => walk(root, 0));
  return out;
};

/** BeeHive keeps a deactivated organisation (it cannot be deleted): INACTIVE, DELETED, ... */
export const isInactive = (node: Pick<OrganisationNode, "status">): boolean =>
  node.status !== null && node.status !== undefined && node.status !== "ACTIVE";

/**
 * Options for a Select limited to the scope, indented by depth. An inactive
 * organisation is listed with `inactiveLabel` and cannot be picked: BeeHive grants
 * no access in it.
 */
export const organisationOptions = (roots: OrganisationNode[], inactiveLabel?: string) =>
  flattenTree(roots).map((o) => ({
    value: o.id,
    label: `${"  ".repeat(o.depth)}${o.name}${isInactive(o) && inactiveLabel ? ` (${inactiveLabel})` : ""}`,
    disabled: isInactive(o),
  }));

export const organisationName = (roots: OrganisationNode[], id: string | null | undefined): string | null =>
  (id && flattenTree(roots).find((o) => o.id === id)?.name) || null;

/** The organisation and every descendant (what `includeDescendants` lists). */
export const subtreeSize = (roots: OrganisationNode[], id: string): number => {
  const node = flattenTree(roots).find((o) => o.id === id);
  return node ? flattenTree([node]).length : 0;
};
